import { useEffect, useState } from "react";
import api from "@/lib/api";
import { withTemplate } from "@/components/TemplatePicker";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { XIcon, PrinterIcon, ReceiptIcon, ProhibitIcon, WarningCircleIcon, FilePdfIcon, EnvelopeIcon } from "@phosphor-icons/react";
import { EmailHistory, SendDocEmailModal } from "@/components/DocEmail";
import CNTraceability from "../_credit/CNTraceability";
import { SectionHeader, CreateButton, DraftsTable, EstadoSelect } from "../_credit/SalesCreditNotes";
import { usePeriodQuery, PeriodToolbar, Pager, fmtEmision } from "@/components/PeriodQuery";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";
import { getToken } from "@/lib/token";

const TIPOS = [
  { v: "intereses_mora", l: "Intereses por mora", c: "bg-red-500/10 text-red-400 border-red-500/30" },
  { v: "cargo_adicional", l: "Cargo adicional", c: "bg-agro-yellow/10 text-agro-yellow border-agro-yellow/30" },
  { v: "ajuste_favor_empresa", l: "Ajuste a favor de empresa", c: "bg-blue-500/10 text-blue-400 border-blue-500/30" },
  { v: "otro", l: "Otro", c: "bg-app text-text-secondary border-border" },
];

/**
 * Sección "Notas de débito" de Ventas y facturación: consultar, buscar, crear (vinculada o no a
 * una factura, con SUBCUENTA), borradores, visualizar, imprimir/exportar PDF y anular.
 * Regla: la ND lleva su propio saldo, se cobra con recibo y aparece en el estado de cuenta.
 */
export default function DebitNotesSection() {
  const { user } = useAuth();
  const puede = ["admin", "accountant"].includes(user?.role);   // emitir/anular ND: administración y contabilidad
  const [drafts, setDrafts] = useState([]);
  const [clients, setClients] = useState([]);
  const [estado, setEstado] = useState("");
  const pq = usePeriodQuery("notas-debito", { estado });
  const filtered = pq.items;
  const [form, setForm] = useState(null);         // {draft?}
  const [openPrint, setOpenPrint] = useState(null);

  const load = async () => {
    try {
      const [c, d] = await Promise.all([api.get("/clients"),
        api.get("/debit-notes-drafts").catch(() => ({ data: [] }))]);
      setClients(c.data || []); setDrafts(d.data || []);
    } catch (e) { toast.error(apiErr(e)); }
    pq.reload();
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const onVoid = async (dn) => {
    if (!window.confirm(`¿Anular nota de débito ${dn.numero}? Se revierte el cargo en la cuenta del cliente y el asiento contable. El correlativo no se reutiliza.`)) return;
    try { await api.post(`/debit-notes/${dn.id}/void`); toast.success(`${dn.numero} anulada`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const emitirBorrador = async (d) => {
    if (!window.confirm("¿Emitir este borrador? Tomará el CAI y el correlativo vigentes y registrará el cargo en la cuenta del cliente.")) return;
    try { const { data } = await api.post(`/debit-notes-drafts/${d.id}/emit`); toast.success(`Emitida ${data.numero}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const borrarBorrador = async (d) => {
    if (!window.confirm("¿Eliminar este borrador?")) return;
    try { await api.delete(`/debit-notes-drafts/${d.id}`); toast.success("Borrador eliminado"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="section-notas-debito">
      <SectionHeader title="NOTAS DE DÉBITO"
        desc="Intereses por mora, cargos adicionales y ajustes. Llevan su propio saldo, se cobran con recibo y aparecen en el estado de cuenta."
        action={puede && <CreateButton onClick={() => setForm({})} testid="new-debit-note-btn" label="CREAR NOTA DE DÉBITO" />} />
      <PeriodToolbar pq={pq} testid="dn-period">
        <EstadoSelect value={estado} onChange={setEstado} />
      </PeriodToolbar>
      {drafts.length > 0 && (
        <DraftsTable drafts={drafts} refLabel="Cliente" testid="dn-drafts" canEdit={puede}
          onEdit={(d) => setForm({ draft: d })} onEmit={emitirBorrador} onDelete={borrarBorrador} />
      )}
      {filtered.length === 0 ? (
        <div className="bg-card border border-border/60 rounded-lg p-10 text-center">
          <ReceiptIcon size={32} className="text-text-muted mx-auto mb-3" weight="duotone" />
          <div className="text-text-secondary text-sm" data-testid="dn-vacio">{pq.loading ? "Cargando…" : pq.vacio}</div>
        </div>
      ) : (
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-sidebar">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-3 pl-5">Número</th><th className="p-3">Fecha</th><th className="p-3">Cliente</th>
                <th className="p-3">Tipo</th><th className="p-3">Factura / SUBCUENTA</th>
                <th className="p-3 text-right">Total</th><th className="p-3 text-right">Saldo</th>
                <th className="p-3">Estado</th><th className="p-3 pr-5 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => {
                const tipoCfg = TIPOS.find((t) => t.v === d.tipo) || TIPOS[3];
                const absorbida = d.factura_id && !d.saldo_propio;
                return (
                  <tr key={d.id} className={`border-t border-border/30 hover:bg-white/[0.02] ${d.estado === "anulada" ? "opacity-50" : ""}`} data-testid={`dn-row-${d.numero}`}>
                    <td className="p-3 pl-5 font-mono text-xs text-agro-green">{d.numero}</td>
                    <td className="p-3 text-xs">{d.fecha_emision ? fmtEmision(d.fecha_emision) : fmtDate(d.fecha)}</td>
                    <td className="p-3 text-sm"><div>{d.cliente_nombre}</div><div className="text-[10px] text-text-muted">{d.cliente_rtn}</div></td>
                    <td className="p-3"><span className={`text-[10px] uppercase px-2 py-0.5 rounded border whitespace-nowrap ${tipoCfg.c}`}>{tipoCfg.l}</span></td>
                    <td className="p-3 text-xs">
                      <div className="font-mono">{d.factura_numero || "—"}</div>
                      <div className="text-[10px] text-text-muted">{d.proyecto_codigo || "Sin SUBCUENTA"}</div>
                    </td>
                    <td className="p-3 text-right text-sm font-medium text-agro-green">{fmtL(d.total)}</td>
                    <td className="p-3 text-right text-xs text-agro-yellow" data-testid={`dn-saldo-${d.numero}`}>
                      {absorbida ? <span title="Emitida con la regla anterior: su saldo está dentro de la factura">En factura</span>
                        : fmtL(d.saldo_pendiente ?? d.total)}
                    </td>
                    <td className="p-3">
                      {d.estado === "emitida"
                        ? <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-agro-green/10 text-agro-green border border-agro-green/30">Emitida</span>
                        : <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/30">Anulada</span>}
                    </td>
                    <td className="p-3 pr-5 text-right whitespace-nowrap">
                      <button onClick={() => abrirPdf(d)} data-testid={`pdf-dn-${d.numero}`} className="p-1.5 text-text-muted hover:text-agro-green" title="PDF (imprimir / exportar)"><FilePdfIcon size={16} /></button>
                      <button onClick={() => setOpenPrint(d)} data-testid={`print-dn-${d.numero}`} className="p-1.5 text-text-muted hover:text-agro-yellow" title="Visualizar"><PrinterIcon size={16} /></button>
                      {d.estado === "emitida" && puede && (
                        <button onClick={() => onVoid(d)} data-testid={`void-dn-${d.numero}`} className="p-1.5 text-text-muted hover:text-red-400" title="Anular"><ProhibitIcon size={16} /></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pager pq={pq} testid="dn-period" />
        </div>
      )}
      {form && (
        <DebitNoteForm clients={clients} draft={form.draft || null} onClose={() => setForm(null)}
          onSaved={(created) => { setForm(null); load(); if (created) setOpenPrint(created); }} />
      )}
      {openPrint && <DebitNotePrint dn={openPrint} onClose={() => setOpenPrint(null)}
        onReload={async () => { const { data } = await api.get(`/debit-notes/${openPrint.id}`); setOpenPrint(data); load(); }} />}
    </div>
  );
}

async function abrirPdf(d) {
  try {
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
    const token = getToken();
    const res = await fetch(withTemplate(`${apiBase}/debit-notes/${d.id}/pdf`, "nota_debito"), { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    window.open(URL.createObjectURL(await res.blob()), "_blank");
  } catch (e) { toast.error(`Error abriendo PDF: ${e.message}`); }
}

function DebitNoteForm({ clients, onClose, onSaved, draft = null }) {
  const draftId = draft?.id || null;
  const [form, setForm] = useState(draft?.ui?.form || {
    cliente_id: "", tipo: "intereses_mora", motivo: "", descripcion: "",
    monto_base: 0, isv_pct: 0, factura_id: "", cai: "",
    fecha_emision_comprobante: "", correlativo: "", proyecto_id: "",
  });
  const [invoices, setInvoices] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  // Factura referida: "ninguna" · "plataforma" (datos automáticos e históricos) · "manual" (se digitan)
  const [refModo, setRefModo] = useState(draft?.ui?.refModo || "ninguna");
  const [facSel, setFacSel] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!form.cliente_id) { setProyectos([]); return; }
    api.get(`/clients/${form.cliente_id}/projects`)
      .then(({ data }) => setProyectos((data || []).filter((p) => !p.virtual))).catch(() => setProyectos([]));
  }, [form.cliente_id]);

  useEffect(() => {
    if (!form.factura_id) { setFacSel(null); return; }
    api.get(`/invoices/${form.factura_id}`).then(({ data }) => setFacSel(data)).catch(() => setFacSel(null));
  }, [form.factura_id]);

  useEffect(() => {
    if (!form.cliente_id) { setInvoices([]); return; }
    api.get(`/clients/${form.cliente_id}/statement`).then((r) => {
      setInvoices((r.data.invoices || []).filter((i) => i.estado !== "anulada"));
    }).catch(() => setInvoices([]));
  }, [form.cliente_id]);

  const isvMonto = Number(form.monto_base || 0) * (Number(form.isv_pct || 0) / 100);
  const total = Number(form.monto_base || 0) + isvMonto;

  const buildPayload = () => ({
    cliente_id: form.cliente_id, tipo: form.tipo,
    motivo: form.motivo, descripcion: form.descripcion,
    monto_base: Number(form.monto_base) || 0, isv_pct: Number(form.isv_pct || 0),
    factura_id: refModo === "plataforma" ? (form.factura_id || null) : null,
    cai: refModo === "manual" ? (form.cai || "").trim().toUpperCase() : "",
    fecha_emision_comprobante: refModo === "manual" ? form.fecha_emision_comprobante : null,
    correlativo: refModo === "manual" ? (form.correlativo || "").trim() : "",
    proyecto_id: refModo === "plataforma" ? null : (form.proyecto_id || null),
  });
  const guardarBorrador = async () => {
    if (!form.cliente_id) return toast.error("Selecciona el cliente");
    setSaving(true);
    try {
      await api.post("/debit-notes-drafts", { id: draftId, payload: buildPayload(), ui: { form, refModo } });
      toast.success("Borrador guardado (sin efecto en saldos)");
      onSaved(null);
    } catch (er) { toast.error(apiErr(er)); }
    finally { setSaving(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.cliente_id) return toast.error("Cliente requerido");
    if (!form.motivo.trim()) return toast.error("Motivo obligatorio");
    if (Number(form.monto_base) <= 0) return toast.error("Monto debe ser > 0");
    if (refModo === "plataforma" && !form.factura_id) return toast.error("Selecciona la factura registrada");
    if (refModo === "manual") {
      if (!/^\d{3}-\d{3}-01-\d{8}$/.test(form.correlativo.trim())) return toast.error("Número de factura: formato 000-000-01-00000000");
      if (!/^[0-9A-F]{6}(-[0-9A-F]{6}){4}-[0-9A-F]{2}$/.test(form.cai.trim().toUpperCase())) return toast.error("CAI: formato XXXXXX-XXXXXX-XXXXXX-XXXXXX-XXXXXX-XX");
      if (!form.fecha_emision_comprobante) return toast.error("Fecha de emisión de la factura");
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      let data;
      if (draftId) {   // borrador: se actualiza y se emite una sola vez
        await api.post("/debit-notes-drafts", { id: draftId, payload, ui: { form, refModo } });
        ({ data } = await api.post(`/debit-notes-drafts/${draftId}/emit`));
      } else {
        ({ data } = await api.post("/debit-notes", payload));
      }
      toast.success(`${data.numero} emitida. Cargo registrado en la cuenta del cliente.`);
      onSaved(data);
    } catch (er) { toast.error(apiErr(er)); }
    finally { setSaving(false); }
  };

  const dm = useModalForm({ value: { form, refModo, facSel }, onClose: onClose, onSave: submit });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...dm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="debit-note-modal">
        <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-yellow">Ventas y facturación · Nota de débito</div>
            <h3 className="font-heading text-lg font-semibold mt-0.5">{draftId ? "Borrador de nota de débito" : "Crear nota de débito"}</h3>
          </div>
          <button aria-label="Cerrar" onClick={dm.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
        </div>
        <form onSubmit={dm.guardar} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Cliente *</label>
              <select
                data-testid="dn-cliente"
                value={form.cliente_id}
                onChange={(e) => setForm({ ...form, cliente_id: e.target.value, factura_id: "" })}
                required
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              >
                <option value="">— Seleccionar cliente —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nombre}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Tipo *</label>
              <select
                value={form.tipo}
                onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              >
                {TIPOS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </div>
          </div>

          <div data-testid="dn-ref">
            <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Factura a la que se aplica</label>
            <div className="grid grid-cols-3 gap-2 mb-2">
              {[["ninguna", "Sin factura"], ["plataforma", "Registrada en la plataforma"], ["manual", "No registrada · manual"]].map(([k, t]) => (
                <button key={k} type="button" data-testid={`dn-ref-${k}`}
                  onClick={() => { setRefModo(k); setForm((f) => ({ ...f, factura_id: "", cai: "", correlativo: "", fecha_emision_comprobante: "" })); }}
                  className={`rounded-md border px-2 py-1.5 text-xs ${refModo === k ? "border-agro-green bg-agro-green/10 text-agro-green font-semibold" : "border-border"}`}>{t}</button>
              ))}
            </div>
            {refModo === "plataforma" && (
              <>
                <select value={form.factura_id} onChange={(e) => setForm({ ...form, factura_id: e.target.value })}
                  disabled={!form.cliente_id} data-testid="dn-factura"
                  className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green disabled:opacity-60">
                  <option value="">{form.cliente_id ? "— Selecciona la factura —" : "Primero selecciona el cliente"}</option>
                  {invoices.map((i) => <option key={i.id} value={i.id}>{i.numero} · {fmtDate(i.fecha)} · {fmtL(i.total)}</option>)}
                </select>
                {facSel && (
                  <div className="mt-2 grid grid-cols-3 gap-2 text-xs bg-agro-green/5 border border-agro-green/30 rounded-md p-2" data-testid="dn-fac-info">
                    <div><div className="text-[10px] uppercase text-text-muted">Número</div><div className="font-mono">{facSel.numero}</div></div>
                    <div className="col-span-2"><div className="text-[10px] uppercase text-text-muted">CAI con que se emitió</div><div className="font-mono text-[11px] truncate" title={facSel.cai_numero || facSel.cai}>{facSel.cai_numero || facSel.cai || "—"}</div></div>
                    <div><div className="text-[10px] uppercase text-text-muted">Fecha de emisión</div><div>{fmtDate(facSel.fecha)}</div></div>
                    <div className="col-span-2"><div className="text-[10px] uppercase text-text-muted">Cliente</div><div>{facSel.cliente_nombre}{facSel.cliente_rtn ? ` · RTN ${facSel.cliente_rtn}` : ""}</div></div>
                  </div>
                )}
                <p className="text-[10px] text-text-muted mt-1">Se usan los datos históricos de la factura. Su saldo pendiente también se incrementará.</p>
              </>
            )}
            {refModo === "manual" && (
              <div className="grid grid-cols-3 gap-2 bg-agro-yellow/5 border border-agro-yellow/40 rounded-md p-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1 block">Número de factura *</label>
                  <input value={form.correlativo} onChange={(e) => setForm({ ...form, correlativo: e.target.value })} placeholder="000-001-01-00000000" data-testid="dn-man-numero" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-xs font-mono" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1 block">CAI *</label>
                  <input value={form.cai} onChange={(e) => setForm({ ...form, cai: e.target.value.toUpperCase() })} placeholder="XXXXXX-…-XX" data-testid="dn-man-cai" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-xs font-mono" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1 block">Fecha de emisión *</label>
                  <input type="date" value={form.fecha_emision_comprobante} onChange={(e) => setForm({ ...form, fecha_emision_comprobante: e.target.value })} data-testid="dn-man-fecha" className="w-full bg-app border border-border rounded-md px-2 py-1.5 text-xs" />
                </div>
                <p className="col-span-3 text-[10px] text-text-muted">Factura emitida fuera de la plataforma: sus datos se validan y se imprimen como referencia.</p>
              </div>
            )}
          </div>

          {proyectos.length > 0 && (
            <div data-testid="dn-proyecto">
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">SUBCUENTA</label>
              {refModo === "plataforma" ? (
                <div className="text-xs text-text-secondary bg-app border border-border rounded-md px-3 py-2">
                  {facSel ? (facSel.proyecto_codigo ? `${facSel.proyecto_codigo} · ${facSel.proyecto_nombre || ""}` : "Sin SUBCUENTA") : "Se toma el de la factura seleccionada"}
                </div>
              ) : (
                <select value={form.proyecto_id || ""} onChange={(e) => setForm({ ...form, proyecto_id: e.target.value })}
                  data-testid="dn-proyecto-sel" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm">
                  <option value="">Sin SUBCUENTA</option>
                  {proyectos.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}
                </select>
              )}
            </div>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Motivo *</label>
            <input
              data-testid="dn-motivo"
              value={form.motivo}
              onChange={(e) => setForm({ ...form, motivo: e.target.value })}
              placeholder="ej. Intereses por mora 30 días"
              required
              className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Descripción / Detalle</label>
            <textarea
              value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              placeholder="Detalle adicional del cargo"
              rows={2}
              className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green resize-none"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Monto base (L.) *</label>
              <input
                data-testid="dn-monto"
                type="number" step="0.01" min="0"
                value={form.monto_base}
                onChange={(e) => setForm({ ...form, monto_base: e.target.value })}
                required
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">ISV %</label>
              <select
                value={form.isv_pct}
                onChange={(e) => setForm({ ...form, isv_pct: e.target.value })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              >
                <option value="0">0% (intereses)</option>
                <option value="15">15% (gravado)</option>
                <option value="18">18% (extraordinario)</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Total</label>
              <div className="bg-agro-green/10 border border-agro-green/30 rounded-md px-3 py-2 text-sm font-semibold text-agro-green">
                {fmtL(total)}
              </div>
            </div>
          </div>

          <div className="bg-agro-yellow/10 border border-agro-yellow/30 rounded-md p-3 text-xs flex items-center gap-2">
            <WarningCircleIcon size={14} className="text-agro-yellow" weight="duotone" />
            <span>La nota de débito lleva <strong>su propio saldo</strong> ({fmtL(total)}): se cobra con recibo y aparece en el estado de cuenta. No modifica el saldo de la factura referida.</span>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={dm.cerrar} className="flex-1 bg-app border border-border hover:bg-white/[0.03] text-text-primary rounded-md py-2.5 text-sm">Cancelar</button>
            <button type="button" onClick={guardarBorrador} disabled={saving || !form.cliente_id} data-testid="dn-save-draft"
              className="flex-1 border border-agro-green/50 text-agro-green rounded-md py-2.5 text-sm hover:bg-agro-green/10 disabled:opacity-40">
              Guardar borrador
            </button>
            <button disabled={dm.saving} type="submit" data-testid="save-dn-btn" className="flex-1 bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md py-2.5 text-sm">
              {saving ? "Emitiendo…" : "Emitir nota de débito"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DebitNotePrint({ dn, onClose, onReload }) {
  const tipoCfg = TIPOS.find((t) => t.v === dn.tipo) || TIPOS[3];
  const [emailOpen, setEmailOpen] = useState(false);
  const vm = useModalForm({ onClose: onClose });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 print:bg-white print:p-0" {...vm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-xl max-h-[92vh] overflow-y-auto print:bg-white print:border-0 print:max-h-none print:max-w-none print:rounded-none" onClick={(e) => e.stopPropagation()} data-testid="dn-print-modal">
        <div className="p-4 border-b border-border flex items-center justify-between print:hidden">
          <h3 className="font-heading text-sm uppercase tracking-wider text-text-muted">Nota de Débito</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                try {
                  const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
                  const token = getToken();
                  const res = await fetch(withTemplate(`${apiBase}/debit-notes/${dn.id}/pdf`, "nota_debito"), {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  if (!res.ok) throw new Error(`HTTP ${res.status}`);
                  const blob = await res.blob();
                  window.open(URL.createObjectURL(blob), "_blank");
                } catch (e) { toast.error(`Error abriendo PDF: ${e.message}`); }
              }}
              data-testid="dn-pdf-official-btn"
              className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-3 py-1.5 text-xs flex items-center gap-1"
              title="Descargar PDF oficial (plantilla CH)"
            >
              <FilePdfIcon size={12} weight="bold" /> PDF oficial
            </button>
            {dn.estado !== "anulada" && (
              <button onClick={() => setEmailOpen(true)} data-testid="dn-send-email"
                className="border border-agro-green/40 text-agro-green rounded-md px-3 py-1.5 text-xs flex items-center gap-1 hover:bg-agro-green/10">
                <EnvelopeIcon size={12} weight="bold" /> Enviar por email
              </button>
            )}
            <button onClick={() => window.print()} className="bg-agro-yellow hover:bg-agro-yellow/80 text-black font-medium rounded-md px-3 py-1.5 text-xs flex items-center gap-1">
              <PrinterIcon size={12} weight="bold" /> Imprimir HTML
            </button>
            <button aria-label="Cerrar" onClick={vm.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={18} /></button>
          </div>
        </div>
        <div className="p-6 voucher-printable bg-card print:bg-white print:text-black">
          <div className="text-center pb-4 border-b-2 border-agro-green print:border-black mb-5">
            <div className="font-heading text-2xl font-bold text-agro-green print:text-black">AGROPORT</div>
            <div className="text-xs text-text-muted print:text-gray-700">ERP · Honduras</div>
            <div className="font-heading text-lg mt-3 uppercase tracking-wider">Nota de Débito</div>
            <div className="font-mono text-sm text-agro-green print:text-black mt-1">{dn.numero}</div>
            {dn.cai && <div className="text-[10px] font-mono text-text-muted print:text-gray-700 mt-0.5">CAI: {dn.cai}</div>}
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Row label="Fecha de emisión" value={fmtDate(dn.fecha)} />
            <Row label="Tipo" value={tipoCfg.l} />
            <Row label="Cliente" value={dn.cliente_nombre} />
            <Row label="RTN" value={dn.cliente_rtn || "—"} />
            {dn.factura_numero && <Row label="Factura asociada" value={dn.factura_numero} />}
            {dn.correlativo && <Row label="Correlativo SAR" value={dn.correlativo} />}
          </div>
          <div className="mt-4 p-3 bg-app print:bg-gray-100 rounded-md text-sm">
            <div className="text-[10px] uppercase tracking-wider text-text-muted print:text-gray-700 mb-1">Motivo</div>
            <div>{dn.motivo}</div>
            {dn.descripcion && <div className="mt-2 text-xs text-text-secondary print:text-gray-700">{dn.descripcion}</div>}
          </div>
          <div className="mt-4 border border-border print:border-black rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b border-border/60 print:border-gray-400">
                  <td className="p-2.5 px-4">{Number(dn.isv_pct) > 0 ? `Gravado ${dn.isv_pct}%` : "Exento"}</td>
                  <td className="p-2.5 px-4 text-right font-medium">{fmtL(dn.monto_base)}</td>
                </tr>
                <tr className="border-b border-border/60 print:border-gray-400">
                  <td className="p-2.5 px-4">ISV ({dn.isv_pct}%)</td>
                  <td className="p-2.5 px-4 text-right">{fmtL(dn.isv_monto)}</td>
                </tr>
                <tr className="bg-agro-green/10 print:bg-gray-100">
                  <td className="p-3 px-4 font-medium uppercase tracking-wider text-xs">Total</td>
                  <td className="p-3 px-4 text-right font-heading text-xl text-agro-green print:text-black">{fmtL(dn.total)}</td>
                </tr>
                <tr className="print:hidden">
                  <td className="p-2.5 px-4 text-xs text-text-secondary">Saldo pendiente (después de notas de crédito)</td>
                  <td className="p-2.5 px-4 text-right text-agro-yellow" data-testid="dn-view-saldo">{fmtL(dn.saldo_pendiente ?? dn.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-6 text-center text-[10px] text-text-muted print:text-gray-500">
            Emitido el {fmtDate(dn.created_at)} · AGROPORT ERP · {dn.created_by}
          </div>
          <div className="print:hidden mt-4">
            <CNTraceability tipo="nota_debito" docId={dn.id} title="Notas de crédito aplicadas a esta nota de débito" />
          </div>
          <div className="print:hidden"><EmailHistory items={dn.email_history} /></div>
        </div>
        {emailOpen && (
          <SendDocEmailModal title="Enviar nota de débito por email" docLabel="Nota de débito"
            endpoint={`/debit-notes/${dn.id}/send-email`} clienteId={dn.cliente_id}
            defaultSubject={`Nota de débito ${dn.numero} · Agroport Honduras`}
            onClose={() => setEmailOpen(false)} onSent={() => onReload && onReload()} />
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-text-muted print:text-gray-600">{label}</div>
      <div className="text-text-primary print:text-black mt-0.5">{value}</div>
    </div>
  );
}
